import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MemberBasic, Member } from '../models/member';
import {
  Container,
  VStack,
  Box,
  Heading,
  Text,
  HStack,
  Icon,
  useColorModeValue,
  Alert,
  AlertIcon,
  Button
} from '@chakra-ui/react';
import { FaIdCard, FaUserPlus, FaSignInAlt } from 'react-icons/fa';
import { useAuth } from '../components/auth';
import LoadingSpinner from '../components/loading-spinner';
import { getMember } from '../services/member-service';
import { BACKOFFICE_BASE_URL, LOGIN_PAGE_ROUTE, QR_CODE_ENDPOINT, REGISTRATION_PAGE_ROUTE } from '../constants';
import { getDate } from '../helpers';
import { MembershipCard } from '@augustocuadro/pacifico-shared';

// Helper function to convert Member to MemberBasic
const convertMemberToMemberBasic = (member: Member): MemberBasic => {
  return {
    id: member.id,
    firstName: member.firstName,
    lastName: member.lastName,
    documentNumber: member.documentNumber,
    memberNumber: member.memberNumber,
    membershipDate: member.membershipDate,
    type: member.memberType?.value || '',
    imagePath: member.imagePath,
    gender: member.gender?.value ?? ''
  };
};

function MyMembershipCredential(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const { memberId } = useParams<{ memberId?: string }>();
  const [member, setMember] = useState<MemberBasic | null>(auth?.user?.selectedMember as MemberBasic);
  const [userId, setUserId] = useState<string | null | undefined>(
    auth?.user?.selectedMember ? (auth.user.selectedMember.userId ?? null) : undefined
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const headerBg = useColorModeValue('green.100', 'green.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.300');
  const isAppUser = auth?.user?.selectedMember && !memberId;

  useEffect(() => {
    if (!auth?.user?.selectedMember && memberId) {
      setLoading(true);
      setError(null);

      getMember(memberId)
        .then((memberData) => {
          if (memberData.response) {
            setMember(convertMemberToMemberBasic(memberData.response));
            setUserId(memberData.response.userId ?? null);
          } else {
            setError('No se pudo encontrar el socio especificado.');
          }
        })
        .catch((err) => {
          console.error('Error al recuperar datos del socio:', err);
          setError('Error al cargar los datos del socio.');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [auth?.user?.selectedMember, memberId]);

  if (loading) {
    return (
      <Container maxW="container.xl" py={6}>
        <LoadingSpinner label="Cargando credencial..." minH="calc(100dvh - 10rem)" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  if (member == null) {
    return (
      <Container maxW="container.xl" py={6}>
        <Alert status="warning">
          <AlertIcon />
          No se encontraron datos del socio para mostrar la credencial.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={6}>
      <VStack spacing={6} align="stretch">
        <Box
          bg={headerBg}
          p={6}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="sm"
        >
          <HStack>
            <Icon as={FaIdCard} color="green.500" boxSize={6} />
            <Heading color={textColor}>Mi credencial de socio</Heading>
          </HStack>
          <Text mt={2} color={secondaryTextColor}>
            Esta es tu credencial digital de socio. Podés mostrarla en el club
            para identificarte.
          </Text>
        </Box>

        {/* Credential Card */}
        <Box
          bg={cardBg}
          p={8}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="lg"
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <MembershipCard
            memberNumber={member.memberNumber.toString()}
            firstName={member.firstName}
            lastName={member.lastName}
            documentNumber={member.documentNumber.toString()}
            imageUrl={member.imagePath ?? ''}
            membershipDate={getDate(new Date(member.membershipDate), 'dd/mm/yyyy') ?? ''}
            memberType={member.type ?? ''}
            gender={member.gender ?? ''}
            qrCodeUrl={QR_CODE_ENDPOINT.replace('{MemberUrl}', `${BACKOFFICE_BASE_URL}/members/status/${member.id}`)}
          />
        </Box>

        {/* User registration / login banner */}
        {!isAppUser && (
          <Box
            bg={headerBg}
            p={6}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="sm"
          >
            <VStack spacing={4}>
              <Text
                color={textColor}
                fontSize={{ base: 'sm', md: 'md' }}
                alignSelf="flex-start"
              >
                <strong>{member.firstName}</strong>
                {userId
                  ? `, notamos que ya creaste tu usuario en la sede virtual de Pacífico. Podés iniciar sesión haciendo click en el siguiente botón.`
                  : `, notamos que todavía no creaste tu usuario en la sede virtual de Pacífico. Te invitamos a que te registres haciendo click en el siguiente botón.`}
              </Text>
              <Button
                colorScheme="green"
                leftIcon={<Icon as={userId ? FaSignInAlt : FaUserPlus} />}
                onClick={() =>
                  navigate(userId ? LOGIN_PAGE_ROUTE : REGISTRATION_PAGE_ROUTE)
                }
                w={{ base: 'full', md: 'auto' }}
              >
                {userId ? 'Iniciar sesión' : 'Registrarme'}
              </Button>
              {!userId && (
                <>
                  <Text
                    color={textColor}
                    fontSize={{ base: 'sm', md: 'md' }}
                    alignSelf="flex-start"
                  >
                    En caso de que ya tengas un usuario y quieras vincular este
                    socio a ese mismo usuario, simplemente iniciá sesión y buscá
                    la opción "Vincular otro socio" en la parte superior derecha
                    de la aplicación.
                  </Text>
                  <Button
                    colorScheme="green"
                    leftIcon={<Icon as={FaSignInAlt} />}
                    onClick={() => navigate(LOGIN_PAGE_ROUTE)}
                    w={{ base: 'full', md: 'auto' }}
                  >
                    Iniciar sesión
                  </Button>
                </>
              )}
            </VStack>
          </Box>
        )}
      </VStack>
    </Container>
  );
}

export default MyMembershipCredential;
